import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { resetPassword, confirmResetPassword } from '@aws-amplify/auth';

function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // 'request' | 'confirm'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      await resetPassword({ username: email });
      setStep('confirm');
      setMessage('Check your email for a reset code.');
      setIsError(false);
    } catch (err) {
      setMessage(err.message || 'Failed to send reset code. Please try again.');
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      setMessage('Password reset successfully! Redirecting to sign in...');
      setIsError(false);
      setTimeout(() => navigate('/SignIn'), 2000);
    } catch (err) {
      setMessage(err.message || 'Failed to reset password. Please try again.');
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-quicksand flex flex-col md:flex-row">
      {/* Left Side - Branding */}
      <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-study-pink via-study-teal to-study-yellow p-12 flex-col justify-center items-center text-center">
        <img
          src="/studyPassLogo2.png"
          alt="StudyPass Logo"
          className="w-32 h-32 mb-8 rounded-full border-4 border-white shadow-lg"
        />
        <h1 className="text-4xl font-bold text-study-black mb-4">
          Reset Your Password
        </h1>
        <p className="text-xl text-study-black max-w-md">
          We'll send a verification code to your email to get you back in.
        </p>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 md:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="md:hidden text-center mb-8">
            <img
              src="/studyPassLogo2.png"
              alt="StudyPass Logo"
              className="w-20 h-20 mx-auto mb-4 rounded-full border-4 border-study-teal shadow-lg"
            />
          </div>

          <h2 className="text-3xl font-bold text-study-black mb-2 text-center md:text-left">
            {step === 'request' ? 'Forgot Password?' : 'Enter Reset Code'}
          </h2>
          <p className="text-gray-600 mb-8 text-center md:text-left">
            {step === 'request'
              ? 'Enter your email and we\'ll send you a reset code.'
              : `We sent a code to ${email}. Enter it below with your new password.`}
          </p>

          {/* Step 1 - Request Code */}
          {step === 'request' && (
            <form onSubmit={handleRequestCode} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-study-black mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                    ✉️
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {message && (
                <div className={`p-4 rounded-xl text-center ${
                  isError
                    ? 'bg-red-100 border border-red-300 text-red-700'
                    : 'bg-green-100 border border-green-300 text-green-700'
                }`}>
                  {message}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-study-teal to-study-pink text-study-black font-bold text-lg py-4 rounded-xl hover:shadow-lg transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⏳</span> Sending...
                  </span>
                ) : (
                  'Send Reset Code'
                )}
              </button>
            </form>
          )}

          {/* Step 2 - Confirm Reset */}
          {step === 'confirm' && (
            <form onSubmit={handleConfirmReset} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-study-black mb-2">
                  Verification Code
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                    🔑
                  </span>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-study-black mb-2">
                  New Password
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                    🔒
                  </span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full pl-12 pr-12 py-3 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {message && (
                <div className={`p-4 rounded-xl text-center ${
                  isError
                    ? 'bg-red-100 border border-red-300 text-red-700'
                    : 'bg-green-100 border border-green-300 text-green-700'
                }`}>
                  {message}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-study-teal to-study-pink text-study-black font-bold text-lg py-4 rounded-xl hover:shadow-lg transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⏳</span> Resetting...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </button>

              <button
                type="button"
                onClick={() => { setStep('request'); setMessage(''); }}
                className="w-full text-gray-500 text-sm hover:text-study-teal transition-all"
              >
                Didn't receive a code? Try again
              </button>
            </form>
          )}

          {/* Back to Sign In */}
          <div className="text-center mt-8">
            <Link
              to="/SignIn"
              className="text-study-teal font-bold hover:text-study-pink transition-all"
            >
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
